import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Spin,
  Empty,
  Popconfirm,
  Tag,
} from 'antd';
import { Plus, Edit2, Trash2, IndianRupee, FileText, Wallet } from 'lucide-react';
import dayjs from 'dayjs';
import useApiMethods from '../../lib/useApiMethods';
import {
  CASH_COLLECTED_SUMMARY,
  CASH_ENTRIES,
  CASH_ENTRY_DETAIL,
} from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';
import { AuthContext } from '../../context/AuthContext';

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);

const CashCollectedTab = () => {
  const { fetch, create, update, remove } = useApiMethods();
  const { hasPermission } = useContext(AuthContext);
  const canEdit = hasPermission('finance_sensitive', 'edit');

  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const monthKey = useMemo(() => selectedMonth.format('YYYY-MM'), [selectedMonth]);
  const monthRange = useMemo(() => ({
    from: selectedMonth.startOf('month').format('YYYY-MM-DD'),
    to: selectedMonth.endOf('month').format('YYYY-MM-DD'),
  }), [selectedMonth]);

  const loadSummary = useCallback(async () => {
    const res = await fetch(CASH_COLLECTED_SUMMARY, { month: monthKey }, setSummaryLoading);
    if (res?.status === 'success') setSummary(res.data);
  }, [monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEntries = useCallback(async () => {
    const res = await fetch(
      CASH_ENTRIES,
      { type: 'credit', date_from: monthRange.from, date_to: monthRange.to, page_size: 200 },
      setEntriesLoading,
    );
    if (res?.status === 'success') {
      setEntries(res.data?.results || []);
    }
  }, [monthRange.from, monthRange.to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSummary();
    loadEntries();
  }, [loadSummary, loadEntries]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    // Default to today if viewing current month, else the 1st of the selected month.
    const isCurrentMonth = selectedMonth.isSame(dayjs(), 'month');
    form.setFieldsValue({
      entry_date: isCurrentMonth ? dayjs() : selectedMonth.startOf('month'),
    });
    setModalOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    form.setFieldsValue({
      amount: Number(entry.amount),
      description: entry.description || '',
      entry_date: entry.entry_date ? dayjs(entry.entry_date) : dayjs(),
      reference_id: entry.reference_id || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      entry_type: 'credit',
      amount: values.amount,
      description: values.description || '',
      entry_date: values.entry_date.format('YYYY-MM-DD'),
      reference_id: values.reference_id || null,
    };
    const res = editing
      ? await update(CASH_ENTRY_DETAIL(editing.id), payload)
      : await create(CASH_ENTRIES, payload);
    if (res?.status === 'success') {
      ToastMsg('success', editing ? 'Entry updated' : 'Entry added');
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      loadSummary();
      loadEntries();
    } else {
      ToastMsg('error', res?.message || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    const res = await remove(CASH_ENTRY_DETAIL(id));
    if (res?.status === 'success') {
      ToastMsg('success', 'Entry deleted');
      loadSummary();
      loadEntries();
    } else {
      ToastMsg('error', res?.message || 'Failed to delete');
    }
  };

  const monthLabel = summary?.month_start
    ? dayjs(summary.month_start).format('MMMM YYYY')
    : dayjs().format('MMMM YYYY');

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-foreground">
            Cash Collected — <span className="text-muted-foreground font-normal">{monthLabel}</span>
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Month</span>
            <DatePicker
              picker="month"
              value={selectedMonth}
              onChange={(v) => v && setSelectedMonth(v.startOf('month'))}
              format="MMM YYYY"
              allowClear={false}
            />
            {!selectedMonth.isSame(dayjs(), 'month') && (
              <Button size="small" onClick={() => setSelectedMonth(dayjs().startOf('month'))}>
                This month
              </Button>
            )}
          </div>
        </div>
        {summaryLoading && !summary ? (
          <div className="flex items-center justify-center py-10"><Spin /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              iconBg="bg-teal-500"
              icon={<FileText className="w-5 h-5 text-white" />}
              title="From CRM (Onboarded receipts)"
              value={formatINR(summary?.crm?.total)}
              breakdown={[
                { label: 'Product', value: formatINR(summary?.crm?.product) },
                { label: 'AMC', value: formatINR(summary?.crm?.amc) },
                { label: 'Receipts', value: summary?.crm?.receipt_count ?? 0 },
              ]}
              error={summary?.crm?.error}
            />
            <KpiCard
              iconBg="bg-indigo-500"
              icon={<Wallet className="w-5 h-5 text-white" />}
              title="Manual entries"
              value={formatINR(summary?.manual?.total)}
              breakdown={[
                { label: 'Entries', value: summary?.manual?.entry_count ?? 0 },
              ]}
            />
            <KpiCard
              iconBg="bg-emerald-500"
              icon={<IndianRupee className="w-5 h-5 text-white" />}
              title="Total this month"
              value={formatINR(summary?.grand_total)}
              highlight
            />
          </div>
        )}
      </div>

      {/* Manual entries table */}
      <div className="rounded-2xl bg-card border border-border shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-foreground">
              Manual Cash Entries
              <span className="text-xs text-muted-foreground font-normal ml-2">· {monthLabel}</span>
            </h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Payments received outside of TrustEdge onboarded receipts
            </p>
          </div>
          {canEdit && (
            <Button type="primary" icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
              Add Entry
            </Button>
          )}
        </div>

        <Table
          loading={entriesLoading}
          dataSource={entries}
          rowKey="id"
          size="middle"
          pagination={{ pageSize: 15, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="No manual entries yet" /> }}
          columns={[
            {
              title: 'Date',
              dataIndex: 'entry_date',
              key: 'entry_date',
              width: 130,
              render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '-',
              sorter: (a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''),
              defaultSortOrder: 'descend',
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              width: 140,
              align: 'right',
              render: (v) => <span className="font-semibold tabular-nums text-emerald-600">{formatINR(Number(v))}</span>,
              sorter: (a, b) => Number(a.amount) - Number(b.amount),
            },
            {
              title: 'Description',
              dataIndex: 'description',
              key: 'description',
              render: (v) => v || <span className="text-muted-foreground">—</span>,
            },
            {
              title: 'Reference',
              dataIndex: 'reference_id',
              key: 'reference_id',
              width: 160,
              render: (v) => v ? <Tag>{v}</Tag> : <span className="text-muted-foreground">—</span>,
            },
            ...(canEdit ? [{
              title: 'Actions',
              key: 'actions',
              width: 120,
              align: 'center',
              render: (_, record) => (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    type="text"
                    size="small"
                    icon={<Edit2 className="w-4 h-4" />}
                    onClick={() => openEdit(record)}
                  />
                  <Popconfirm
                    title="Delete this entry?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                  >
                    <Button type="text" size="small" danger icon={<Trash2 className="w-4 h-4" />} />
                  </Popconfirm>
                </div>
              ),
            }] : []),
          ]}
        />
      </div>

      <Modal
        title={editing ? 'Edit Cash Entry' : 'Add Cash Entry'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditing(null); }}
        onOk={handleSave}
        okText={editing ? 'Update' : 'Add'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Amount (₹)"
            name="amount"
            rules={[{ required: true, message: 'Amount is required' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => v.replace(/,/g, '')}
            />
          </Form.Item>
          <Form.Item
            label="Date received"
            name="entry_date"
            rules={[{ required: true, message: 'Date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="What was this payment for?" />
          </Form.Item>
          <Form.Item label="Reference / receipt no." name="reference_id">
            <Input placeholder="Optional — bank ref, invoice no., etc." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const KpiCard = ({ icon, iconBg, title, value, breakdown, highlight, error }) => (
  <div className={`rounded-2xl border shadow-sm p-5 ${
    highlight
      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-500/40'
      : 'bg-card border-border'
  }`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
    </div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{title}</div>
    <div className={`text-2xl font-bold tabular-nums mb-2 ${
      highlight ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
    }`}>
      {value}
    </div>
    {breakdown && (
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {breakdown.map((b, i) => (
          <span key={i}>
            <span className="text-muted-foreground">{b.label}:</span>{' '}
            <span className="text-foreground font-medium tabular-nums">{b.value}</span>
          </span>
        ))}
      </div>
    )}
    {error && (
      <div className="mt-2 text-xs text-amber-600">CRM unreachable: {error}</div>
    )}
  </div>
);

export default CashCollectedTab;
